/**
 * Core Download Hook
 * Unified file download logic with progress tracking for both Platform and Team contexts
 */

import { useCallback, useState } from 'react'

import * as fsOps from '../fs-operations'
import { FSContext, IDownloadTask, IFile } from '../types'

let downloadIdCounter = 0

function generateDownloadId(): string {
  return `download_${Date.now()}_${++downloadIdCounter}`
}

/**
 * Core download hook - accepts FSContext for platform/team abstraction
 * @param context - FS Context (Platform or Team)
 */
export function useDownloadCore(context: FSContext) {
  const [downloadList, setDownloadList] = useState<IDownloadTask[]>([])

  const updateTask = useCallback((taskId: string, updates: Partial<IDownloadTask>) => {
    setDownloadList((prev) =>
      prev.map((task) => {
        if (task.id !== taskId) return task

        // Calculate speed when loaded is updated
        if (updates.loaded !== undefined && updates.loaded > 0) {
          const elapsedSeconds = (Date.now() - task.startTime) / 1000
          if (elapsedSeconds > 0) {
            updates.speed = updates.loaded / elapsedSeconds
          }
        }

        return { ...task, ...updates }
      }),
    )
  }, [])

  const removeTask = useCallback((taskId: string) => {
    setDownloadList((prev) => prev.filter((task) => task.id !== taskId))
  }, [])

  const downloadFile = useCallback(
    async (file: IFile, domain: number) => {
      const taskId = generateDownloadId()
      const abortController = new AbortController()

      const task: IDownloadTask = {
        id: taskId,
        name: file.name,
        size: file.size,
        loaded: 0,
        status: 'downloading',
        startTime: Date.now(),
        speed: 0,
        type: 'single',
        abortController,
      }

      setDownloadList((prev) => [...prev, task])

      try {
        await fsOps.downloadWithProgress(
          context,
          { path: file.path, domain, abortController },
          (loaded, total) => {
            updateTask(taskId, { loaded, size: total || file.size })
          },
        )
        updateTask(taskId, { status: 'completed', loaded: file.size })
      } catch (error: any) {
        if (error.name === 'AbortError' || error.message === 'Download cancelled') {
          updateTask(taskId, { status: 'cancelled' })
        } else {
          console.error('Download error:', error)
          updateTask(taskId, { status: 'error' })
        }
      }
    },
    [context, updateTask, removeTask],
  )

  const downloadAsZip = useCallback(
    async (files: IFile[], domain: number) => {
      const taskId = generateDownloadId()
      const abortController = new AbortController()

      const totalSize = files.reduce((acc, f) => acc + (f.size || 0), 0)
      const task: IDownloadTask = {
        id: taskId,
        name: `${files.length} files`,
        size: totalSize,
        loaded: 0,
        status: 'downloading',
        startTime: Date.now(),
        speed: 0,
        type: 'zip',
        files,
        abortController,
      }

      setDownloadList((prev) => [...prev, task])

      try {
        await fsOps.downloadAsZipWithProgress(
          context,
          { files, domain, abortController },
          (loaded, total) => {
            updateTask(taskId, { loaded, size: total || totalSize })
          },
        )
        updateTask(taskId, { status: 'completed', loaded: totalSize })
      } catch (error: any) {
        if (error.name === 'AbortError' || error.message === 'Download cancelled') {
          updateTask(taskId, { status: 'cancelled' })
        } else {
          console.error('Download error:', error)
          updateTask(taskId, { status: 'error' })
        }
      }
    },
    [context, updateTask, removeTask],
  )

  const cancelDownload = useCallback((taskId: string) => {
    setDownloadList((prev) => {
      const task = prev.find((t) => t.id === taskId)
      if (task?.abortController) {
        task.abortController.abort()
      }
      return prev.map((t) => (t.id === taskId ? { ...t, status: 'cancelled' as const } : t))
    })
  }, [])

  return {
    downloadList,
    downloadFile,
    downloadAsZip,
    cancelDownload,
  }
}
